import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {getImageByPreview} from 'api/library';
import {inviteStudents} from 'api/course';
import {UserObject} from 'stores/UserStore';
import CourseBuilderContext from 'components/CourseBuilder/CourseBuilderContext';
import Modal, {ModalProps} from 'components/Modal';
import Button, {ButtonVariant} from 'components/Button';
import UserPicker from 'components/UserPicker';
import {InputVariant} from 'components/Input';
import ControlButton from 'components/ControlButton';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {IconTrash} from 'icons';
import {toast} from 'react-toastify';

export type InviteModalProps = Pick<ModalProps, 'onBlur'> & {
  onInvite?(): void;
}

const Header = styled.div`
  display: grid;
  justify-content: flex-start;
`;

const Title = styled.div`
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
`;

const Note = styled.span`
  margin-top: 20px;
  display: inline-block;
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
  font-size: 13px;
  font-weight: 400;
  line-height: 17px;
`;

const CourseContainer = styled.div`
  margin: 18px 0;
  column-gap: 10px;
  grid-template-columns: 106px 1fr;
  align-items: center;
  display: grid;
  padding: 10px;
  border-radius: 5px;
  background: ${({theme}) => `
    ${theme.variables.accentBackgroundColor};
  `};
`;

const CoursePreview = styled.img`
  width: 100%;
  height: 60px;
  border-radius: 5px;
  object-fit: cover;
`;

const CourseTitle = styled.span`
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
`;

const Footer = styled.div`
  display: grid;
  justify-content: flex-end;
`;

const Content = styled.div`
  gap: 16px;
  display: grid;
`;

const Users = styled.div`
  display: grid;
`;

const User = styled.div`
  gap: 16px;
  align-items: center;
  grid-auto-flow: column;
  grid-template-columns: max-content 1fr max-content;
  display: grid;
  
  border-radius: 5px;
  box-sizing: border-box;
  padding: 11px 14px;
  font-size: 14px;
  
  &:nth-child(2n + 2) {
    background-color: ${({theme}) => `
      ${theme.variables.accentBackgroundColor};
    `};
  }
`;

const UserAvatar = styled.img`
  border-radius: 50%;
  height: 30px;
  width: 30px;
`;

/**
 * This component renders a student invitation modal window.
 * @param {InviteModalProps} props
 * @return {React.ReactElement<InviteModalProps>}
 * @constructor
 */
export default function InviteModal(
    props: InviteModalProps,
): React.ReactElement<InviteModalProps> {
  const {course} = React.useContext(CourseBuilderContext);

  const [
    users,
    setUsers,
  ] = React.useState<UserObject[]>([]);

  /**
   * Render the header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    return (
      <Header>
        <Title>
          Добавить учеников на курс
        </Title>
      </Header>
    );
  }

  /**
   * Render the footer.
   * @return {JSX.Element}
   */
  function renderFooter(): JSX.Element {
    /**
     * Render submit button.
     * @return {JSX.Element}
     */
    function renderSubmit(): JSX.Element {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        course?.id && (
          inviteStudents(course.id, users.map((user) => (
            user.id
          ))).then((response) => {
            validateResponse(response);

            if (response && !isError(response)) {
              toast.success('Приглашения отправлены!');
              props.onBlur && props.onBlur();
              props.onInvite && props.onInvite();
            }
          })
        );
      }

      return (
        <Button
          type="button"
          variant={ButtonVariant.Primary}
          onClick={handleClick}
          disabled={users.length === 0}
        >
          Отправить приглашения на курс
        </Button>
      );
    }

    return (
      <Footer>
        {renderSubmit()}
      </Footer>
    );
  }

  /**
   * Render the course card.
   * @return {JSX.Element}
   */
  function renderCourse(): JSX.Element {
    return (
      <CourseContainer>
        <CoursePreview
          src={getImageByPreview(course?.preview)}
        />
        <CourseTitle>
          {course?.title}
        </CourseTitle>
      </CourseContainer>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render users.
     * @return {JSX.Element}
     */
    function renderUsers(): JSX.Element {
      /**
       * Render the user.
       * @param {UserObject} user
       * @param {number} index
       * @return {JSX.Element}
       */
      function renderUser(
          user: UserObject,
          index: number,
      ): JSX.Element {
        /**
         * Render delete control.
         * @return {JSX.Element}
         */
        function renderDeleteControl(): JSX.Element {
          /**
           * This click event handler.
           */
          function handleClick(): void {
            setUsers(users.filter((stateUser) => (
              stateUser !== user
            )));
          }

          return (
            <ControlButton onClick={handleClick}>
              <IconTrash />
            </ControlButton>
          );
        }

        return (
          <User key={index}>
            <UserAvatar
              src={getImageByPreview(user.avatar)}
            />
            {user.name}
            {renderDeleteControl()}
          </User>
        );
      }

      return (
        <Users>
          {users.map((user, index) => (
            renderUser(user, index)
          ))}
        </Users>
      );
    }

    /**
     * Render user picker.
     * @return {JSX.Element}
     */
    function renderUserPicker(): JSX.Element {
      /**
       * This select event handler.
       * @param {UserObject} user
       */
      function handleSelect(user: UserObject): void {
        setUsers([
          ...users,
          user,
        ]);
      }

      return (
        <UserPicker
          inputProps={{
            variant: InputVariant.Accent,
            placeholder: 'Введите имя пользователя',
          }}
          onSelect={handleSelect}
        />
      );
    }

    return (
      <Content>
        {renderUserPicker()}
        {renderUsers()}
      </Content>
    );
  }

  return (
    <Modal
      renderHeader={renderHeader()}
      renderFooter={renderFooter()}
      onBlur={props.onBlur}
      fullScreen
    >
      <Note>
        Укажите E-mail ученика или найдите его по имени
      </Note>
      {renderCourse()}
      {renderContent()}
    </Modal>
  );
}
